import { Component, computed, effect, inject, input, linkedSignal, model } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { _last, _numberEnumEntries, _stringMapValues, IsoDateTime } from '@naturalcycles/js-lib'
import { GoalBreakpointOption, shortQuizGoalOptions } from '@naturalcycles/shared'
import { countrySharedUtil } from '@naturalcycles/shared/country'
import { SignalStore } from '@src/app/core/store/signalStore.js'
import { SharedModule } from '@src/app/shared/shared.module.js'
import { Page } from '@src/app/shared/typings/enum/pages.js'
import { DefaultSections } from '@src/app/shared/typings/enum/quiz.js'
import { env } from '@src/environments/environment.js'
import { ButtonComponentModule } from '../../button/button.component.module.js'
import { SearchableSelectComponent } from '../../input/searchable-select/searchable-select.component.js'

interface BaseAdminMenuAction<T, P = string> {
  type: T
  payload?: P
}

export type AdminMenuAction =
  | BaseAdminMenuAction<
      | 'restart'
      | 'clearCountry'
      | 'triggerPaymentWithNonce'
      | 'clearExperiments'
      | 'logOutUser'
      | 'toggleInspector'
      | 'resetTime',
      string
    >
  | BaseAdminMenuAction<'changeCountry', { country: string; region: string }>
  | BaseAdminMenuAction<'setExperiment', { experiment?: string; bucket?: string }>
  | BaseAdminMenuAction<'skip', { goal: GoalBreakpointOption }>
  | BaseAdminMenuAction<'skipToSection', { goal: GoalBreakpointOption; sectionIndex: number }>
  | BaseAdminMenuAction<'timeTravel', { time: IsoDateTime }>

@Component({
  templateUrl: 'admin-menu-modal.component.html',
  selector: 'admin-menu-modal',
  styleUrls: ['admin-menu-modal.component.scss'],
  imports: [ButtonComponentModule, FormsModule, SharedModule, SearchableSelectComponent],
})
export class AdminMenuModalComponent {
  private store = inject(SignalStore)
  paymentNonce = 'fake-valid-nonce'

  // Splits a string on camel case capital letters and capitalizes the first letter of each word
  prettifyLabel = (label: string): string =>
    label
      .split(/(?=[A-Z])/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')

  // For use in template
  goalEnum = GoalBreakpointOption
  goals = _numberEnumEntries(GoalBreakpointOption).map(([label, value]) => ({
    label: this.prettifyLabel(label),
    value,
  }))

  protected api = input.required<{ select: (b: AdminMenuAction) => void }>()
  protected isProduction = !!env.prod

  // Countries that always should be displayed at the top of the list
  private topCountries = ['US', 'GB', 'AU', 'CA', 'DE', 'JP', 'BR', 'MX', 'RU']

  countries = _stringMapValues(countrySharedUtil.getAllCountryData())
    .map(c => ({
      label: c.name,
      value: c.code.toUpperCase(),
    }))
    .toSorted((a, b) => {
      if (this.topCountries.includes(a.value)) return -1
      if (this.topCountries.includes(b.value)) return 1
      return a.label.localeCompare(b.label)
    })

  // Override country
  currentCountry: string = localStorage.getItem('e2eCountry') || ''
  selectedCountry: string = this.currentCountry
  currentRegion: string = localStorage.getItem('e2eRegion') || ''
  selectedRegion: string = this.currentRegion

  // Set experiment
  experiment?: string
  bucket?: string

  reddot = computed(() => this.store.$reddot())

  fertilityGoal = computed(() => this.store.$quiz().data?.fertilityGoal)

  // Skip quiz
  goal = linkedSignal(() => this.fertilityGoal() || GoalBreakpointOption.prevent)

  currentSectionIndex = computed(() => {
    const currentSlug = _last(location.pathname.split('/'))
    return this.store.$quiz().flow.findIndex(section => section.includes(currentSlug as Page))
  })

  sectionToSkipToIndex = linkedSignal(() => {
    const currentSectionIndex = this.currentSectionIndex()
    const { flow } = this.store.$quiz()
    if (flow.length <= currentSectionIndex) {
      return currentSectionIndex
    }

    return currentSectionIndex + 1
  })

  sectionToSkipToName = computed(() => {
    const sectionIndex = this.sectionToSkipToIndex()
    return Object.values(DefaultSections)[sectionIndex] || 'Unknown Section'
  })

  sectionToSkipToError = computed<string | undefined>(() => {
    const sectionToSkipTo = this.sectionToSkipToIndex()
    const goal = this.goal()

    if (shortQuizGoalOptions.includes(goal) && sectionToSkipTo > 0) {
      const sectionName = Object.values(DefaultSections)[sectionToSkipTo]!
      return `Skipping to section ${sectionName} is not allowed for short goal ${GoalBreakpointOption[goal]}. Please skip quiz instead.`
    }
  })

  sectionOptions = _numberEnumEntries(DefaultSections).map(([label], i) => ({
    label: this.prettifyLabel(label),
    value: i,
  }))

  // Time travel
  overrideTime = computed(() => this.reddot()?.overrideTime)
  overrideTimeFormModel = model(this.overrideTime())

  // Api URL
  environmentApiUrl = env.apiUrl
  apiUrl = linkedSignal(() => this.reddot()?.overrideApiUrl)
  setAdminApiUrl(apiUrl: string | undefined): void {
    this.store.$reddot.update(reddot => ({
      ...reddot,
      overrideApiUrl: apiUrl,
    }))
  }

  onChangeCountry(country: string): void {
    if (country !== 'US') {
      this.selectedRegion = ''
    }
  }

  public getAsIsoDateTime(): IsoDateTime {
    return (this.overrideTimeFormModel() + ':00Z') as IsoDateTime
  }

  constructor() {
    effect(() => {
      this.overrideTimeFormModel.set(this.overrideTime())
    })
  }
}
